import React, { FC, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useMount } from "react-use";

import { StandardLayout } from "@/components/layouts";
import { ItemList } from "@/components/organisms";
import { policyholderApi } from "@/shared/apis";
import { AppRoute, PolicyHolder, PolicyHolderPageState, TodoCategory } from "@/shared/domain";
import { useNavigation, usePolicyholders, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

const getTodoTypeFromUrl = (pathname: string) => {
    switch (pathname) {
        case AppRoute.Liability:
            return TodoCategory.LIABILITY;
        case AppRoute.CustomerCard:
            return TodoCategory.CUSTOMER_CARD;
        case AppRoute.DocumentsToSign:
            return TodoCategory.DOCUMENTS_TO_SIGN;
        case AppRoute.CommunicationSalary:
            return TodoCategory.COMMUNICATION_SALARY;
        case AppRoute.Tariffication:
            return TodoCategory.TARIFFICATION;
        case AppRoute.DataVerification:
        case AppRoute.DataVerificationCompany:
            return TodoCategory.DATA_VERIFICATION;
        default:
            return null;
    }
};

const PolicyHolderPage: FC = () => {
    const {
        state: { type: typeState, policyholders: policyholdersSubset },
        pathname,
    } = useRouterHook<PolicyHolderPageState>();
    const { t } = useTranslation();
    const { getAllPolicyholders } = usePolicyholders();
    const {
        navToCustomerCardDetails,
        navToCommunicationSalaryDetails,
        navToLiabilityDetails,
        navToTarifficationDetails,
        navToDataVerificationDetails,
    } = useNavigation();
    const type: TodoCategory = typeState || getTodoTypeFromUrl(pathname) || TodoCategory.CUSTOMER_CARD;
    let allPolicyholders = getAllPolicyholders();
    if (policyholdersSubset && policyholdersSubset.length > 0) {
        allPolicyholders = allPolicyholders.filter((it) => policyholdersSubset.indexOf(it.policyHolderId) !== -1);
    }

    const { data: policyholdersQuery = [], isFetching } = useQuery(
        ["policyholdersPerAccessRight", { type, allPolicyholders }],
        () => policyholderApi.getPolicyholdersPerAccessRightType(type, allPolicyholders),
        {
            suspense: false,
        },
    );
    let policyholders = policyholdersQuery;

    policyholders = policyholders.filter(
        (ph) =>
            (type === TodoCategory.CUSTOMER_CARD &&
                ph.accessRights.canAccessCustomerForm &&
                ph.accessRights.customerCardRequested) ||
            (type === TodoCategory.DATA_VERIFICATION && !ph.isPhysicalPerson) ||
            (type !== TodoCategory.CUSTOMER_CARD && type !== TodoCategory.DATA_VERIFICATION),
    );

    const { incUsageCount } = useUserFeedback();

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("insuredGood", "Insured Goods");
    });

    const handlePolicyholder = (policyholder: PolicyHolder, replace: boolean) => {
        switch (type) {
            case TodoCategory.CUSTOMER_CARD:
                return navToCustomerCardDetails(policyholder.policyHolderId, replace);
            case TodoCategory.LIABILITY:
                return navToLiabilityDetails(policyholder.policyHolderId, replace);
            case TodoCategory.COMMUNICATION_SALARY:
                return navToCommunicationSalaryDetails(policyholder.policyHolderId, replace);
            case TodoCategory.TARIFFICATION:
                return navToTarifficationDetails(policyholder.policyHolderId, null, replace);
            case TodoCategory.DATA_VERIFICATION:
                return navToDataVerificationDetails(policyholder.policyHolderId, replace);
            default:
                return null;
        }
    };

    useEffect(() => {
        if (!isFetching && policyholders.length === 1) {
            handlePolicyholder(policyholders[0], true);
        }
    }, [isFetching]);

    const policyholderList = policyholders.map((ph) => ({ id: ph.policyHolderId.toString(), label: ph.fullName }));

    const handleSelectItem = (itemId: string) => {
        const policyholder = policyholders.find((ph) => ph.policyHolderId.toString() === itemId);
        handlePolicyholder(policyholder, false);
    };

    return (
        <StandardLayout title={t("Common.Policyholder")}>
            <ItemList items={policyholderList} onSelectItem={handleSelectItem}></ItemList>
        </StandardLayout>
    );
};

export default PolicyHolderPage;
